import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const LIST_CLIENTS = gql`
  query ListClients {
    listClients {
      id
      firstName
      lastName
      email
      phoneNumber
      createdAt
    }
  }
`;

const LIST_CASES = gql`
  query ListCases {
    listCases {
      items {
        id
        clientId
        status
      }
    }
  }
`;

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  createdAt: string;
}

interface CaseItem {
  id: string;
  clientId: string;
  status: string;
}

interface ListClientsData {
  listClients: Client[];
}

interface ListCasesData {
  listCases: {
    items: CaseItem[];
  };
}

export function ClientsWidget() {
  const navigate = useNavigate();
  const { data: clientsData, loading: clientsLoading, error: clientsError } = useQuery<ListClientsData>(LIST_CLIENTS);
  const { data: casesData } = useQuery<ListCasesData>(LIST_CASES, {
    variables: { limit: 1000 },
  });

  const clients = clientsData?.listClients || [];
  const allCases = casesData?.listCases?.items || [];

  // Sort clients alphabetically by name
  const sortedClients = [...clients].sort((a, b) => {
    const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
    const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });

  // Count active cases per client
  const getActiveCasesCount = (clientId: string) => {
    return allCases.filter(
      (c: any) => c.clientId === clientId && c.status !== 'CLOSED'
    ).length;
  };

  if (clientsLoading) {
    return (
      <Card className="inquiry-card">
        <CardHeader>
          <CardTitle>All Clients</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (clientsError) {
    return (
      <Card className="inquiry-card">
        <CardHeader>
          <CardTitle>All Clients</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="text-4xl mb-3">⚠️</div>
            <p className="text-gray-600 text-sm">Deploy infrastructure to enable this feature</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="inquiry-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>All Clients ({clients.length})</CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/clients')}
        >
          Manage
        </Button>
      </CardHeader>
      <CardContent>
        {sortedClients.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-3">👥</div>
            <p className="text-gray-500 text-sm">No clients yet</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
            {sortedClients.map((client: any) => {
              const activeCases = getActiveCasesCount(client.id);
              return (
                <div
                  key={client.id}
                  onClick={() => navigate(`/clients/${client.id}`)}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-aalaw-red hover:bg-gray-50 cursor-pointer transition-all"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-aalaw-red flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-semibold text-sm">
                        {client.firstName?.[0]?.toUpperCase() || client.email?.[0]?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-gray-900 truncate">
                        {client.firstName} {client.lastName}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{client.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {activeCases > 0 && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                        {activeCases} {activeCases === 1 ? 'case' : 'cases'}
                      </span>
                    )}
                    <span className="text-gray-400">›</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
